import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import { t } from "i18next";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import DeleteAccessGroupModal from "../../modal/DeleteAccessGroupModal";
import { cn } from "../../../utils/cn";
import { TABLE_IDS } from "../../../constants/common";

interface AccessGroup {
  n_AccessGroupID: number;
  s_AccessGroupName_En: string;
  s_AccessGroupName_Ar: string;
  s_LocationName_En: string;
  s_LocationName_Ar: string;
  s_BuildingName_En: string;
  s_BuildingName_Ar: string;
  n_IsAreaDefaultGroup: number;
  s_IsAreaDefaultGroup_En: string;
  s_IsAreaDefaultGroup_Ar: string;
  s_FRDevicesNames_En: string;
  s_FRDevicesNames_Ar: string;
}

interface AccessGroupsTableProps {
  accessGroups: AccessGroup[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  setSelectedAccessGroupId: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const AccessGroupsTable = ({
  accessGroups,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
  setSelectedAccessGroupId,
}: AccessGroupsTableProps) => {
  const { i18n } = useTranslation();

  const [isDeleteAccessGroupModalOpen, setIsDeleteAccessGroupModalOpen] =
    useState(false);
  const [selectedDeletedAccessGroupId, setSelectedDeletedAccessGroupId] =
    useState(0);

  const handleDeleteFRDevice = useCallback(async (accessGroupId: number) => {
    setIsDeleteAccessGroupModalOpen(true);
    setSelectedDeletedAccessGroupId(accessGroupId);
  }, []);

  const columns: ColumnDef<AccessGroup>[] = useMemo(
    () => [
      {
        accessorKey: "n_AccessGroupID",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("accessGroupId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() =>
                setSelectedAccessGroupId(row.original.n_AccessGroupID)
              }
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_AccessGroupID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_AccessGroupName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("accessGroupNameEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_AccessGroupName_En}</span>;
        },
      },
      {
        accessorKey: "s_AccessGroupName_Ar",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("accessGroupNameAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_AccessGroupName_Ar}</span>;
        },
      },
      {
        accessorKey: "s_FRDevicesNames_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("devices")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_FRDevicesNames_En
                : row.original.s_FRDevicesNames_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_IsAreaDefaultGroup_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("locationDefaultGroup")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 mx-auto max-w-[128px] w-[128px]",
                row.original.n_IsAreaDefaultGroup === 1
                  ? "bg-green-600"
                  : "bg-red-500"
              )}
            >
              <p className={cn("text-sm whitespace-normal text-white")}>
                {i18n.language === "en"
                  ? row.original.s_IsAreaDefaultGroup_En
                  : row.original.s_IsAreaDefaultGroup_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() => handleDeleteFRDevice(row.original.n_AccessGroupID)}
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteFRDevice, i18n.language, setSelectedAccessGroupId]
  );

  return (
    <>
      <TableWrapper
        data={accessGroups}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        tableId={TABLE_IDS.ACCESS_GROUPS}
        isSearch
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteAccessGroupModal
        accessGroupId={selectedDeletedAccessGroupId}
        isOpen={isDeleteAccessGroupModalOpen}
        onOpenChange={() => setIsDeleteAccessGroupModalOpen(false)}
      />
    </>
  );
};

export default React.memo(AccessGroupsTable);
